import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { LoggerService } from 'src/app/services/logger.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ModalComponent, ModalModel } from '../modal/modal.component';
import { isFormulaType, isNumberType } from 'src/app/shared/formula/formula.types';

@Component({
    selector: 'app-custom-form-fields',
    templateUrl: './custom-form-fields.component.html',
    styleUrls: ['./custom-form-fields.component.scss'],
    standalone: false
})
export class CustomFormFieldsComponent implements OnInit, OnChanges, OnDestroy {
  /** Card #566: the Formula / Number types by the ids the server sent, for the template. */
  readonly isFormulaType = isFormulaType;
  readonly isNumberType = isNumberType;
  public customFielsDataFrom: FormGroup;
  @Input() customerCustomFieldArr: any;
  /** Per-field apply-on-quote state (source of truth for UI + save). */
  formulaApplyByFieldId: Record<string, boolean> = {};
  @Input() itemId: number | string | null = null;
  @Output() removeCustomField: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateCustomeOrder: EventEmitter<any> = new EventEmitter<any>();
  @Output() updateCustomField: EventEmitter<any> = new EventEmitter<any>();
  @Output() editCustomField: EventEmitter<any> = new EventEmitter<any>();
  /** The user flipped a Formula field's "Calculate on quotes" switch (the item form seeds a recipe). */
  @Output() formulaApplyChange = new EventEmitter<{ fieldId: string; on: boolean }>();
  @Input() editdeletshow: any;

  private _customDataFieldViewData: any[] = [];
  /** develop: dropdown options were range-sorted whenever saved values were provided (even []). */
  private hasViewDataInput = false;
  /**
   * Last saved value written into each non-formula control. A control is (re)patched only when
   * it is new or its saved value changed, so values arriving in either order reach the form and a
   * parent re-emitting the same saved rows never wipes what the user typed.
   */
  private patchedSavedValues = new Map<string, unknown>();
  private userEditedFormulaApply = new Set<string>();
  /** valueChanges subscription per apply_formula_* control, so removing a control ends it. */
  private formulaControlSubs = new Map<string, Subscription>();
  private _destroyed = false;
  private _unsubscribeAll: Subject<any> = new Subject();

  @Input()
  set customDataFieldViewData(value: any) {
    this._customDataFieldViewData = Array.isArray(value) ? value : [];
    this.hasViewDataInput = this.hasViewDataInput || !!value;
    this.tryInitializeFormulaApplyState(true);
    // Patching happens in ngOnChanges (a setter input still appears in SimpleChanges), never here:
    // this runs inside the parent's change detection.
  }
  get customDataFieldViewData(): any[] {
    return this._customDataFieldViewData;
  }

  constructor(
    private translateService: TranslateService,
    public dialog: MatDialog,
    private formBuilder: FormBuilder,
    private cdr: ChangeDetectorRef,
    @Optional() private logger?: LoggerService,
  ) {
    this.customFielsDataFrom = this.formBuilder.group({});
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.customerCustomFieldArr?.currentValue || changes.itemId?.currentValue) {
      if (changes.itemId && changes.itemId.previousValue !== changes.itemId.currentValue) {
        this.resetFormulaApplyState();
        this.resetValueControls();
        this.patchedSavedValues.clear();
      } else if (changes.customerCustomFieldArr) {
        this.pruneFormulaApplyState();
      }
      this.ensureNonFormulaFormControls();
      // Don't force-reset Apply on quote when formula metadata is saved —
      // the update response is the request body and lacks per-item flags.
      this.tryInitializeFormulaApplyState(!!changes.itemId);
      this.ensureFormulaControlsForFields();
      this.patchNonFormulaFormValues();
    } else if (changes.customDataFieldViewData) {
      // Saved values can arrive AFTER the field list (organization dialog sets them on a 2s timer,
      // contact edit loads them in parallel). They must still reach the form, or Save posts them empty.
      this.ensureNonFormulaFormControls();
      this.ensureFormulaControlsForFields();
      this.patchNonFormulaFormValues();
    }
  }

  ngOnInit(): void {}

  ngOnDestroy(): void {
    this._destroyed = true;
    this.formulaControlSubs.forEach((sub) => sub.unsubscribe());
    this.formulaControlSubs.clear();
    this._unsubscribeAll.next(undefined);
    this._unsubscribeAll.complete();
  }

  isFormulaApplyReady(): boolean {
    return (this.customDataFieldViewData?.length ?? 0) > 0 || this.itemId != null;
  }

  coerceApplyFormula(value: unknown): boolean {
    return value === true || value === 1 || value === '1' || value === 'true';
  }

  /** Pure template lookup: controls are created only in ngOnChanges (ensureFormulaControlsForFields). */
  getFormulaControl(field: any): FormControl | null {
    return (this.customFielsDataFrom.get('apply_formula_' + field?.id) as FormControl) ?? null;
  }

  private ensureFormulaControlsForFields(): void {
    for (const field of this.customerCustomFieldArr || []) {
      if (isFormulaType(field.field_type_id)) {
        this.ensureFormulaControl(field);
      }
    }
    this.syncFormulaControlsFromState();
  }

  private ensureFormulaControl(field: any): void {
    const fieldId = String(field.id);
    const controlName = 'apply_formula_' + fieldId;
    if (!(fieldId in this.formulaApplyByFieldId)) {
      this.formulaApplyByFieldId[fieldId] = this.readSavedApplyFormula(field);
    }
    if (this.customFielsDataFrom.contains(controlName)) {
      field.apply_formula = this.formulaApplyByFieldId[fieldId];
      return;
    }
    const control = new FormControl(this.formulaApplyByFieldId[fieldId]);
    this.customFielsDataFrom.addControl(controlName, control);
    const sub = control.valueChanges.pipe(takeUntil(this._unsubscribeAll)).subscribe((value) => {
      const coerced = this.coerceApplyFormula(value);
      this.formulaApplyByFieldId[fieldId] = coerced;
      // The CURRENT field object for this id: the list may have been replaced since creation.
      const live = (this.customerCustomFieldArr || []).find((f) => String(f?.id) === fieldId);
      if (live) {
        live.apply_formula = coerced;
      }
      this.userEditedFormulaApply.add(fieldId);
      this.formulaApplyChange.emit({ fieldId, on: coerced });
    });
    this.formulaControlSubs.set(controlName, sub);
    field.apply_formula = this.formulaApplyByFieldId[fieldId];
  }

  /** Read apply_formula for item save — parent calls this. */
  getFormulaApply(fieldId: string | number): boolean {
    const id = String(fieldId);
    if (id in this.formulaApplyByFieldId) {
      return this.formulaApplyByFieldId[id];
    }
    const field = (this.customerCustomFieldArr || []).find((f) => String(f.id) === id);
    return field ? this.readSavedApplyFormula(field) : false;
  }

  /** True once the user flipped this field's Apply on quote toggle in this form. */
  hasUserEditedFormulaApply(fieldId: string | number): boolean {
    return this.userEditedFormulaApply.has(String(fieldId));
  }

  getFormValues(): Record<string, unknown> {
    return this.customFielsDataFrom?.getRawValue?.() || {};
  }

  /**
   * Re-sync toggles from saved item data (initial load only — preserves user edits).
   * The parent calls this from ngAfterViewInit / QueryList.changes, i.e. during change detection,
   * so the work is deferred to a microtask and only marks for check.
   */
  refreshFormulaApplyToggles(): void {
    void Promise.resolve().then(() => {
      if (this._destroyed) {
        return;
      }
      this.tryInitializeFormulaApplyState(false);
      this.syncFormulaControlsFromState();
      this.cdr.markForCheck();
    }).catch((err) => {
      // Never an unhandled rejection; the toggles simply keep their current state.
      this.logger?.log('custom-fields: refresh formula toggles failed', err?.message || 'failed');
    });
  }

  private tryInitializeFormulaApplyState(force: boolean): void {
    if (!this.customerCustomFieldArr?.length) {
      return;
    }

    const hasItemValues = (this.customDataFieldViewData?.length ?? 0) > 0;
    const hasItemId = this.itemId != null;
    if (!hasItemValues && !hasItemId) {
      return;
    }

    let changed = false;
    for (const field of this.customerCustomFieldArr) {
      if (!isFormulaType(field.field_type_id)) {
        continue;
      }
      const fieldId = String(field.id);
      if (this.userEditedFormulaApply.has(fieldId)) {
        continue;
      }

      const savedValue = this.readSavedApplyFormulaValue(field);
      if (savedValue === null && fieldId in this.formulaApplyByFieldId) {
        continue;
      }
      const saved = savedValue ?? false;
      if (this.formulaApplyByFieldId[fieldId] !== saved) {
        this.formulaApplyByFieldId[fieldId] = saved;
        field.apply_formula = saved;
        changed = true;
      }
    }

    if (changed || force) {
      this.syncFormulaControlsFromState();
    }
  }

  private syncFormulaControlsFromState(): void {
    for (const field of this.customerCustomFieldArr || []) {
      if (!isFormulaType(field.field_type_id)) {
        continue;
      }
      const fieldId = String(field.id);
      const controlName = 'apply_formula_' + fieldId;
      const value = this.formulaApplyByFieldId[fieldId] ?? false;
      field.apply_formula = value;

      if (this.customFielsDataFrom.contains(controlName)) {
        const control = this.customFielsDataFrom.controls[controlName];
        if (control.value !== value) {
          control.setValue(value, { emitEvent: false });
        }
      }
    }
  }

  private hasApplyFormulaValue(value: unknown): boolean {
    return value !== undefined && value !== null;
  }

  private readSavedApplyFormula(field: any): boolean {
    return this.readSavedApplyFormulaValue(field) ?? false;
  }

  private readSavedApplyFormulaValue(field: any): boolean | null {
    const fieldId = String(field.id);

    const fromView = (this.customDataFieldViewData || []).find(
      (d) => String(d.customerCustomFieldId || d.custom_field_id) === fieldId,
    );

    const fromFieldList =
      this.itemId != null
        ? (field.itemsCustomFieldData || []).find(
            (d) => String(d.itemId) === String(this.itemId),
          )
        : null;

    if (fromView != null && this.hasApplyFormulaValue(fromView.apply_formula)) {
      return this.coerceApplyFormula(fromView.apply_formula);
    }

    if (fromFieldList != null && this.hasApplyFormulaValue(fromFieldList.apply_formula)) {
      return this.coerceApplyFormula(fromFieldList.apply_formula);
    }

    return null;
  }

  /**
   * Another item: clear every non-formula value control, so a field item B has no saved row for
   * never shows (or saves) item A's value. Checkbox / radio controls go back to null, the rest to ''
   * (what ensureNonFormulaFormControls creates them with).
   */
  private resetValueControls(): void {
    for (const [name, control] of Object.entries(this.customFielsDataFrom.controls)) {
      if (!name.startsWith('value_')) {
        continue;
      }
      const isChoice = name.endsWith('_radio') || control.value === null || typeof control.value === 'boolean';
      control.reset(isChoice ? null : '', { emitEvent: false });
    }
  }

  private removeFormulaControl(controlName: string): void {
    this.formulaControlSubs.get(controlName)?.unsubscribe();
    this.formulaControlSubs.delete(controlName);
    this.customFielsDataFrom.removeControl(controlName);
  }

  private resetFormulaApplyState(): void {
    this.formulaApplyByFieldId = {};
    this.userEditedFormulaApply.clear();
    for (const name of Object.keys(this.customFielsDataFrom.controls)) {
      if (name.startsWith('apply_formula_')) {
        this.removeFormulaControl(name);
      }
    }
  }

  private pruneFormulaApplyState(): void {
    const liveIds = new Set(
      (this.customerCustomFieldArr || [])
        .filter((field) => isFormulaType(field?.field_type_id))
        .map((field) => String(field.id)),
    );
    for (const fieldId of Object.keys(this.formulaApplyByFieldId)) {
      if (liveIds.has(fieldId)) {
        continue;
      }
      delete this.formulaApplyByFieldId[fieldId];
      this.userEditedFormulaApply.delete(fieldId);
      const controlName = 'apply_formula_' + fieldId;
      if (this.customFielsDataFrom.contains(controlName)) {
        this.removeFormulaControl(controlName);
      }
    }
  }

  private ensureNonFormulaFormControls(): void {
    if (!this.customerCustomFieldArr?.length) {
      return;
    }
    for (const field of this.customerCustomFieldArr) {
      const typeId = String(field.field_type_id);
      if (isFormulaType(typeId)) {
        continue;
      }
      if (typeId === '2') {
        field.customer_custom_field_values?.forEach((element) => {
          const name = 'value_' + field.id + '_' + element.value;
          if (!this.customFielsDataFrom.contains(name)) {
            this.customFielsDataFrom.addControl(name, new FormControl());
          }
        });
      } else if (typeId === '4') {
        const name = 'value_' + field.id + '_radio';
        if (!this.customFielsDataFrom.contains(name)) {
          this.customFielsDataFrom.addControl(name, new FormControl());
        }
      } else if (typeId === '5') {
        const name = 'value_' + field.id + '_dropdown';
        if (!this.customFielsDataFrom.contains(name)) {
          this.customFielsDataFrom.addControl(name, new FormControl(''));
        }
      } else if (typeId === '3') {
        const name = 'value_' + field.id + '_date';
        if (!this.customFielsDataFrom.contains(name)) {
          this.customFielsDataFrom.addControl(name, new FormControl(''));
        }
      } else if (typeId === '6') {
        const name = 'value_' + field.id + '_color';
        if (!this.customFielsDataFrom.contains(name)) {
          this.customFielsDataFrom.addControl(name, new FormControl(''));
        }
      } else {
        const name = 'value_' + field.id;
        if (!this.customFielsDataFrom.contains(name)) {
          this.customFielsDataFrom.addControl(name, new FormControl(''));
        }
      }
    }
  }

  /** develop's behaviour: dropdown (type '5') options sorted by numeric range ("1-5", "6-10", "+20"). */
  private sortDropdownOptionsByRange(): void {
    function extractRange(value: string): { start: number; end: number } {
      if (value.startsWith('+')) {
        const num = parseInt(value.substring(1), 10);
        return { start: num, end: Infinity };
      }

      const match = value.match(/(\d+)\s*-\s*(\d+)/);
      if (match) {
        return { start: parseInt(match[1], 10), end: parseInt(match[2], 10) };
      }

      const num = parseInt(value.trim(), 10);
      return { start: num || 0, end: num || 0 };
    }

    (this.customerCustomFieldArr || []).forEach((field) => {
      if (field.field_type_id === '5' && Array.isArray(field.customer_custom_field_values)) {
        const current = field.customer_custom_field_values;
        const sorted = [...current].sort((a, b) => {
          const rangeA = extractRange(a.value);
          const rangeB = extractRange(b.value);
          if (rangeA.start !== rangeB.start) {
            return rangeA.start - rangeB.start;
          }
          return rangeA.end - rangeB.end;
        });
        // Keep the same array when already in order: a fresh reference on every change-detection
        // pass (all-leads binds the field list through a method call) re-renders the options.
        if (sorted.some((v, i) => v !== current[i])) {
          field.customer_custom_field_values = sorted;
        }
      }
    });
  }

  private savedValueControlName(data: any, fieldId: unknown): string | null {
    const typeId = String(data.field_type_id);
    if (typeId === '2') return 'value_' + fieldId + '_' + data.value;
    if (typeId === '4') return 'value_' + fieldId + '_radio';
    if (typeId === '5') return 'value_' + fieldId + '_dropdown';
    if (typeId === '3') return 'value_' + fieldId + '_date';
    if (typeId === '6') return 'value_' + fieldId + '_color';
    if (typeId === '1' || isNumberType(typeId)) return 'value_' + fieldId;
    return null;
  }

  private patchNonFormulaFormValues(): void {
    if (!this.customerCustomFieldArr?.length) {
      return;
    }
    if (this.hasViewDataInput) {
      this.sortDropdownOptionsByRange();
    }
    if (!this.customDataFieldViewData?.length) {
      return;
    }

    let patched = false;
    for (const data of this.customDataFieldViewData) {
      const fieldId = data.customerCustomFieldId ? data.customerCustomFieldId : data.custom_field_id;
      const controlName = this.savedValueControlName(data, fieldId);
      const control = controlName ? this.customFielsDataFrom.controls[controlName] : null;
      if (control) {
        // Checkbox rows mark their option ticked; every other type carries its value.
        const saved = String(data.field_type_id) === '2' ? true : data.value;
        const alreadyPatched =
          this.patchedSavedValues.has(controlName) && this.patchedSavedValues.get(controlName) === saved;
        if (!alreadyPatched) {
          control.setValue(saved);
          this.patchedSavedValues.set(controlName, saved);
          patched = true;
        }
      }

      this.customerCustomFieldArr.forEach((customFieldData, index) => {
        if (fieldId == customFieldData.id) {
          this.customerCustomFieldArr[index].value = data.value;
        }
      });
    }

    if (patched) {
      // Runs from ngOnChanges, inside the parent's change detection: mark, don't detect.
      this.cdr.markForCheck();
    }
  }

  async removeItem(customField, index, type) {
    const dialogData = new ModalModel(this.translateService.instant('PROFILE.CONFIRMDELETE'));
    const dialogRef = this.dialog.open(ModalComponent, {
      width: '450px',
      data: dialogData,
    });
    dialogRef.afterClosed().pipe(takeUntil(this._unsubscribeAll)).subscribe(async (dialogResult) => {
      if (dialogResult && type == 'removeCustomField') {
        this.removeCustomField.emit({ customField, index });
      }
    });
  }

  updateSelectChange(event, controlName) {
    if (event.value === null) {
      this.customFielsDataFrom[controlName] = null;
    } else {
      this.customFielsDataFrom[controlName] = event.value;
    }
    this.customFielsDataFrom.controls[controlName].setValue(event.value);
  }

  onColorChange(event: Event, controlName) {
    const colorInput = event.target as HTMLInputElement;
    this.customFielsDataFrom.controls[controlName].setValue(colorInput.value);
  }

  editField(customField, index) {
    this.editCustomField.emit({ isEdit: true, index, customField });
  }

  drop(event: CdkDragDrop<any>) {
    this.customerCustomFieldArr[event.previousContainer.data.index] = event.container.data.item;
    this.customerCustomFieldArr[event.container.data.index] = event.previousContainer.data.item;
    this.updateCustomeOrder.emit(this.customerCustomFieldArr);
  }
}
